/**
 * Shared presentation config for `CompanyChangeEvent` items — used by the
 * /historie timeline and the "Poslední změny" card on Přehled.
 */
import {
	Banknote,
	Briefcase,
	FileSignature,
	FileText,
	History,
	MapPin,
	Percent,
	Tag,
	UserMinus,
	UserPlus,
	Users,
	UserX,
} from "lucide-react";
import type { CompanyChangeEvent } from "~/types/company";

export interface ChangeEventConfig {
	label: string;
	icon: typeof UserPlus;
	iconClasses: string;
}

export const CHANGE_EVENT_CONFIG: Record<string, ChangeEventConfig> = {
	OFFICER_ADDED: {
		label: "Zapsán jednatel",
		icon: UserPlus,
		iconClasses: "bg-green-50 text-green-600",
	},
	OFFICER_REMOVED: {
		label: "Vymazán jednatel",
		icon: UserMinus,
		iconClasses: "bg-red-50 text-red-600",
	},
	SHAREHOLDER_ADDED: {
		label: "Zapsán společník",
		icon: Users,
		iconClasses: "bg-green-50 text-green-600",
	},
	SHAREHOLDER_REMOVED: {
		label: "Vymazán společník",
		icon: UserX,
		iconClasses: "bg-red-50 text-red-600",
	},
	SHAREHOLDER_OWNERSHIP_CHANGED: {
		label: "Změna podílu společníka",
		icon: Percent,
		iconClasses: "bg-primary-50 text-primary-600",
	},
	SEAT_CHANGED: {
		label: "Změna sídla",
		icon: MapPin,
		iconClasses: "bg-primary-50 text-primary-600",
	},
	CAPITAL_CHANGED: {
		label: "Změna základního kapitálu",
		icon: Banknote,
		iconClasses: "bg-primary-50 text-primary-600",
	},
	NAME_CHANGED: {
		label: "Změna názvu",
		icon: Tag,
		iconClasses: "bg-primary-50 text-primary-600",
	},
	FILE_NUMBER_CHANGED: {
		label: "Změna spisové značky",
		icon: FileText,
		iconClasses: "bg-primary-50 text-primary-600",
	},
	BUSINESS_PURPOSE_CHANGED: {
		label: "Změna předmětu podnikání",
		icon: Briefcase,
		iconClasses: "bg-primary-50 text-primary-600",
	},
	WAY_OF_ACTING_CHANGED: {
		label: "Změna způsobu jednání",
		icon: FileSignature,
		iconClasses: "bg-primary-50 text-primary-600",
	},
};

export const FALLBACK_CHANGE_EVENT_CONFIG: ChangeEventConfig = {
	label: "Změna v rejstříku",
	icon: History,
	iconClasses: "bg-gray-100 text-gray-600",
};

export function changeEventConfig(eventType: string): ChangeEventConfig {
	return CHANGE_EVENT_CONFIG[eventType] ?? FALLBACK_CHANGE_EVENT_CONFIG;
}

const OFFICER_ROLE_LABELS: Record<string, string> = {
	STATUTORY_DIRECTOR: "jednatel",
	BOARD_MEMBER: "člen představenstva",
	BOARD_CHAIR: "předseda představenstva",
	SUPERVISORY_BOARD_MEMBER: "člen dozorčí rady",
	SUPERVISORY_BOARD_CHAIR: "předseda dozorčí rady",
	PROXY: "prokurista",
	LIQUIDATOR: "likvidátor",
	MANAGING_DIRECTOR: "ředitel",
};

/** Event types whose `details` JSON carries `{old, new}` string values. */
const OLD_NEW_EVENT_TYPES = new Set([
	"SEAT_CHANGED",
	"CAPITAL_CHANGED",
	"NAME_CHANGED",
	"FILE_NUMBER_CHANGED",
	"BUSINESS_PURPOSE_CHANGED",
	"WAY_OF_ACTING_CHANGED",
]);

function formatShare(value: unknown): string {
	return typeof value === "number" ? `${value} %` : "—";
}

function asNonEmptyString(value: unknown): string | null {
	return typeof value === "string" && value.trim() ? value : null;
}

/** Human-readable detail line for a change event. Returns null when there is nothing to show. */
export function describeChangeEventDetails(event: CompanyChangeEvent): string | null {
	if (!event.details || typeof event.details !== "object") return null;
	const details = event.details as Record<string, unknown>;
	const parts: string[] = [];

	if (typeof details.entityName === "string" && details.entityName) {
		parts.push(details.entityName);
	}

	if (event.eventType === "SHAREHOLDER_OWNERSHIP_CHANGED") {
		parts.push(`podíl ${formatShare(details.before)} → ${formatShare(details.after)}`);
	} else if (OLD_NEW_EVENT_TYPES.has(event.eventType)) {
		const oldValue = asNonEmptyString(details.old);
		const newValue = asNonEmptyString(details.new);
		if (oldValue && newValue) {
			parts.push(`z „${oldValue}“ na „${newValue}“`);
		} else if (newValue) {
			parts.push(`nově „${newValue}“`);
		} else if (oldValue) {
			parts.push(`dříve „${oldValue}“`);
		}
	} else if (typeof details.ownershipPercentage === "number") {
		parts.push(`podíl ${details.ownershipPercentage} %`);
	}

	if (typeof details.role === "string") {
		parts.push(`funkce: ${OFFICER_ROLE_LABELS[details.role] ?? details.role}`);
	}

	return parts.length > 0 ? parts.join(" · ") : null;
}
